/**
 * Builds the multi-sheet Excel/Word/PDF export for the Financial Analysis agent.
 *
 * The old export was a flat "Field / Value" dump of the summary counts. The
 * on-screen worksheets carry the period-indexed financial data (P&L, balance
 * sheet, ratios, cash flow, statutory liabilities, aged payables) plus the 17
 * insolvency indicators. This builder mirrors that:
 *   - "Summary"            — high-level counts + estimated insolvency date.
 *                          Excel only: Word/PDF skip this Field/Value dump.
 *   - one sheet per period type ("Annual" / "Quarterly" / "Monthly") laid out
 *     as a metric × period matrix, sectioned like the UI worksheet.
 *   - "Indicators"         — one row per assessed indicator. Omitted from
 *                          Excel: the analysis column is narrative. Still in
 *                          Word/PDF.
 *   - "Indicator Evidence" — one row per supporting value.
 *
 * Money/ratios are written as real numbers so the practitioner can chart and
 * compute natively (the on-screen charts are driven by exactly these series).
 *
 * MOR-978: each period sheet also carries the worksheet's chart specs
 * (profit & loss, net assets, liquidity ratios, cash flow, statutory
 * liabilities, aged payables) so the Word/PDF exports include the graphs the
 * practitioner sees on screen — they attach these to creditors' reports.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import {
  EXPORT_CHART_COLORS,
  type ExportChart,
} from "@/utils/export/exportCharts";
import { displayPeriodLabel } from "./granularity";
import type { FinancialPeriodEntryUI } from "./types";

/** The subset of the task's computed summary the export surfaces. */
export interface FinancialSummaryOutput {
  status?: string;
  estimatedInsolvencyDate?: string | null;
  totalIndicatorsMet?: number;
  totalIndicatorsAssessed?: number;
  documentsAnalysed?: number;
  agentNarrative?: string;
}

export interface FinancialIndicatorRow {
  _id: string;
  indicatorKey: string;
  indicatorName: string;
  category: string;
  assessment: "met" | "not_met" | "insufficient_data";
  confidence: "high" | "medium" | "low";
  reasoning: string;
  earliestIndicationDate?: string | null;
  supportingEvidence?: Array<{
    periodLabel?: string;
    metric?: string;
    value?: number;
    note?: string;
  }>;
  isEdited?: boolean;
}

interface MetricRow {
  label: string;
  field: keyof FinancialPeriodEntryUI | string;
}

interface MetricSection {
  title: string;
  rows: MetricRow[];
}

/** Sections mirror the FinancialWorksheetPanel block tables, in display order. */
const SECTIONS: MetricSection[] = [
  {
    title: "Continued losses",
    rows: [{ label: "Net Profit / (Loss)", field: "netProfitAfterTax" }],
  },
  {
    title: "Net asset analysis",
    rows: [
      { label: "Total Assets", field: "totalAssets" },
      { label: "Total Liabilities", field: "totalLiabilities" },
      { label: "Net Assets", field: "netAssets" },
    ],
  },
  {
    title: "Short term debt & liquidity",
    rows: [
      { label: "Current Assets", field: "totalCurrentAssets" },
      { label: "Current Liabilities", field: "totalCurrentLiabilities" },
      { label: "Working Capital", field: "workingCapital" },
      { label: "Current Ratio", field: "currentRatio" },
      { label: "Debt Ratio", field: "debtRatio" },
    ],
  },
  {
    title: "Cash flow",
    rows: [
      { label: "Operating Cash Flow", field: "operatingCashFlow" },
      { label: "Investing Cash Flow", field: "investingCashFlow" },
      { label: "Financing Cash Flow", field: "financingCashFlow" },
      { label: "Net Cash Change", field: "netCashFlow" },
    ],
  },
  {
    title: "Statutory liabilities",
    rows: [
      { label: "Tax Liabilities", field: "taxLiabilities" },
      { label: "Super Liabilities", field: "superannuationLiabilities" },
    ],
  },
  {
    title: "Non-current assets & related parties",
    rows: [
      { label: "PP&E", field: "propertyPlantEquipment" },
      { label: "Intangible Assets", field: "intangibleAssets" },
      { label: "Total Non-Current Assets", field: "totalNonCurrentAssets" },
      { label: "RP Receivables", field: "relatedPartyReceivables" },
      { label: "RP Payables", field: "relatedPartyPayables" },
    ],
  },
  {
    title: "Aged payables",
    rows: [
      { label: "Current", field: "payablesCurrent" },
      { label: "30 Days", field: "payables30Days" },
      { label: "60 Days", field: "payables60Days" },
      { label: "90+ Days", field: "payables90PlusDays" },
    ],
  },
  {
    title: "Aged receivables",
    rows: [
      { label: "Current", field: "receivablesCurrent" },
      { label: "30 Days", field: "receivables30Days" },
      { label: "60 Days", field: "receivables60Days" },
      { label: "90+ Days", field: "receivables90PlusDays" },
    ],
  },
  {
    title: "Income statement",
    rows: [
      { label: "Revenue", field: "revenue" },
      { label: "Cost of Goods Sold", field: "costOfGoodsSold" },
      { label: "Gross Profit", field: "grossProfit" },
      { label: "Operating Expenses", field: "operatingExpenses" },
      { label: "EBITDA", field: "ebitda" },
      { label: "D&A", field: "depreciationAmortisation" },
      { label: "EBIT", field: "ebit" },
      { label: "Interest Expense", field: "interestExpense" },
      { label: "Profit Before Tax", field: "netProfitBeforeTax" },
      { label: "Income Tax", field: "incomeTax" },
      { label: "Net Profit After Tax", field: "netProfitAfterTax" },
    ],
  },
  {
    title: "Balance sheet",
    rows: [
      { label: "Cash & Equivalents", field: "cashAndEquivalents" },
      { label: "Trade Receivables", field: "tradeReceivables" },
      { label: "Inventory", field: "inventory" },
      { label: "Other Current Assets", field: "otherCurrentAssets" },
      { label: "Total Current Assets", field: "totalCurrentAssets" },
      { label: "PP&E", field: "propertyPlantEquipment" },
      { label: "Intangible Assets", field: "intangibleAssets" },
      { label: "Other Non-Current Assets", field: "otherNonCurrentAssets" },
      { label: "Total Non-Current Assets", field: "totalNonCurrentAssets" },
      { label: "Total Assets", field: "totalAssets" },
      { label: "Trade Payables", field: "tradePayables" },
      { label: "Short-Term Borrowings", field: "shortTermBorrowings" },
      {
        label: "Current Portion of LT Debt",
        field: "currentPortionLongTermDebt",
      },
      { label: "Other Current Liabilities", field: "otherCurrentLiabilities" },
      { label: "Total Current Liabilities", field: "totalCurrentLiabilities" },
      { label: "Long-Term Borrowings", field: "longTermBorrowings" },
      {
        label: "Other Non-Current Liabilities",
        field: "otherNonCurrentLiabilities",
      },
      {
        label: "Total Non-Current Liabilities",
        field: "totalNonCurrentLiabilities",
      },
      { label: "Total Liabilities", field: "totalLiabilities" },
      { label: "Net Assets", field: "netAssets" },
      { label: "Retained Earnings", field: "retainedEarnings" },
      { label: "Total Equity", field: "totalEquity" },
    ],
  },
];

const PERIOD_TYPE_TITLES: Record<string, string> = {
  annual: "Annual",
  quarterly: "Quarterly",
  monthly: "Monthly",
  weekly: "Weekly",
};

const ASSESSMENT_LABELS: Record<string, string> = {
  met: "Met",
  not_met: "Not Met",
  insufficient_data: "Insufficient Data",
};

const CATEGORY_LABELS: Record<string, string> = {
  primary_test: "Primary Tests",
  financial_analysis: "Financial Analysis",
  creditors_payables: "Creditors & Payables",
  financing: "Financing",
  asset_analysis: "Asset Analysis",
};

function sortPeriods(arr: FinancialPeriodEntryUI[]): FinancialPeriodEntryUI[] {
  return [...arr].sort((a, b) =>
    (a.endDate ?? a.periodLabel).localeCompare(b.endDate ?? b.periodLabel),
  );
}

function cumulative(values: Array<number | null>): Array<number | null> {
  let sum = 0;
  return values.map((v) => {
    if (v == null) return null;
    sum += v;
    return sum;
  });
}

/** Series of one numeric field across the periods, in display order. */
function fieldSeries(
  periods: FinancialPeriodEntryUI[],
  field: keyof FinancialPeriodEntryUI | string,
): Array<number | null> {
  return periods.map((p) => {
    const v = p[field];
    return typeof v === "number" ? v : null;
  });
}

function hasAny(
  periods: FinancialPeriodEntryUI[],
  ...fields: string[]
): boolean {
  return fields.some((f) => periods.some((p) => p[f] != null));
}

/**
 * The worksheet's chart set for one period type, mirroring
 * FinancialWorksheetPanel's inline charts: same series, same colours (the
 * light-theme --chart-* palette), same visibility rules — a chart needs at
 * least two periods and one populated series.
 */
function periodCharts(periods: FinancialPeriodEntryUI[]): ExportChart[] {
  if (periods.length < 2) return [];
  const labels = periods.map((p) => displayPeriodLabel(p));
  const C = EXPORT_CHART_COLORS;
  const charts: ExportChart[] = [];

  if (hasAny(periods, "netProfitAfterTax")) {
    const pnl = fieldSeries(periods, "netProfitAfterTax");
    charts.push({
      title: "Continued losses — profit / (loss) with cumulative trend",
      labels,
      series: [
        {
          label: "Profit / (Loss)",
          kind: "bar",
          color: C.chart1,
          negativeColor: C.chart4,
          values: pnl,
        },
        {
          label: "Cumulative",
          kind: "line",
          color: C.chart2,
          values: cumulative(pnl),
        },
      ],
    });
  }

  if (hasAny(periods, "totalAssets", "totalLiabilities")) {
    charts.push({
      title: "Net asset analysis",
      labels,
      series: [
        {
          label: "Total Assets",
          kind: "bar",
          color: C.chart1,
          values: fieldSeries(periods, "totalAssets"),
        },
        {
          label: "Total Liabilities",
          kind: "bar",
          color: C.chart3,
          values: fieldSeries(periods, "totalLiabilities"),
        },
        {
          label: "Net Assets",
          kind: "line",
          color: C.chart2,
          values: fieldSeries(periods, "netAssets"),
        },
      ],
    });
  }

  if (hasAny(periods, "currentRatio", "debtRatio")) {
    charts.push({
      title: "Liquidity — current ratio and debt ratio",
      labels,
      valueFormat: "ratio",
      referenceLineAt: 1,
      series: [
        {
          label: "Current Ratio",
          kind: "line",
          color: C.chart2,
          values: fieldSeries(periods, "currentRatio"),
        },
        {
          label: "Debt Ratio",
          kind: "line",
          color: C.chart1,
          values: fieldSeries(periods, "debtRatio"),
        },
      ],
    });
  }

  if (hasAny(periods, "operatingCashFlow", "netCashFlow")) {
    const net = fieldSeries(periods, "netCashFlow");
    charts.push({
      title: "Cash flow — net cash change with cumulative trend",
      labels,
      series: [
        {
          label: "Net Cash Change",
          kind: "bar",
          color: C.chart1,
          negativeColor: C.chart4,
          values: net,
        },
        {
          label: "Cumulative",
          kind: "line",
          color: C.chart2,
          values: cumulative(net),
        },
      ],
    });
  }

  if (hasAny(periods, "taxLiabilities", "superannuationLiabilities")) {
    charts.push({
      title: "Statutory liabilities",
      labels,
      series: [
        {
          label: "Taxation",
          kind: "stacked-bar",
          color: C.chart1,
          values: fieldSeries(periods, "taxLiabilities"),
        },
        {
          label: "Superannuation",
          kind: "stacked-bar",
          color: C.chart3,
          values: fieldSeries(periods, "superannuationLiabilities"),
        },
      ],
    });
  }

  if (hasAny(periods, "payablesCurrent", "payables30Days")) {
    charts.push({
      title: "Aged payables",
      labels,
      series: [
        {
          label: "0-30 days",
          kind: "stacked-bar",
          color: C.chart1,
          values: fieldSeries(periods, "payablesCurrent"),
        },
        {
          label: "31-60 days",
          kind: "stacked-bar",
          color: C.chart2,
          values: fieldSeries(periods, "payables30Days"),
        },
        {
          label: "61-90 days",
          kind: "stacked-bar",
          color: C.chart5,
          values: fieldSeries(periods, "payables60Days"),
        },
        {
          label: "90+ days",
          kind: "stacked-bar",
          color: C.chart4,
          values: fieldSeries(periods, "payables90PlusDays"),
        },
      ],
    });
  }

  return charts;
}

/**
 * One metric × period matrix sheet per period type. Section labels become their
 * own row (indexed in `sectionRows` so Word/PDF can style them); only metrics
 * with at least one value across the periods are emitted. The worksheet's
 * charts ride along for the Word/PDF exports.
 */
function periodSheet(
  title: string,
  periods: FinancialPeriodEntryUI[],
): ExportSheet | null {
  if (periods.length === 0) return null;
  const headers = ["Metric", ...periods.map((p) => displayPeriodLabel(p))];
  const rows: ExportSheet["rows"] = [];
  const sectionRows: number[] = [];

  for (const section of SECTIONS) {
    const visibleRows = section.rows.filter((r) =>
      periods.some((p) => p[r.field] != null),
    );
    if (visibleRows.length === 0) continue;

    sectionRows.push(rows.length);
    rows.push([section.title, ...periods.map(() => "")]);
    for (const r of visibleRows) {
      rows.push([
        r.label,
        ...periods.map((p) => {
          // Every `MetricRow.field` above names a numeric metric on
          // `FinancialPeriodEntryUI`; the `String(v)` branch is a defensive
          // fallback (the field type is a plain string key so TS can't
          // prove that statically) rather than an expected runtime path.
          const v = p[r.field];
          if (v == null) return "";
          return typeof v === "number" ? v : String(v);
        }),
      ]);
    }

    // Mirror the chart cumulative series the UI overlays on these sections.
    if (section.title === "Continued losses") {
      const series = periods.map((p) => p.netProfitAfterTax ?? null);
      if (series.some((v) => v != null)) {
        rows.push([
          "Cumulative Net Profit / (Loss)",
          ...cumulative(series).map((v) => (v == null ? "" : v)),
        ]);
      }
    }
    if (section.title === "Cash flow") {
      const series = periods.map((p) => p.netCashFlow ?? null);
      if (series.some((v) => v != null)) {
        rows.push([
          "Cumulative Net Cash Change",
          ...cumulative(series).map((v) => (v == null ? "" : v)),
        ]);
      }
    }
  }

  return {
    name: title,
    headers,
    rows,
    sectionRows,
    charts: periodCharts(periods),
  };
}

function summarySheet(
  output: FinancialSummaryOutput | undefined,
  periods: FinancialPeriodEntryUI[],
): ExportSheet {
  const annual = periods.filter((p) => p.periodType === "annual").length;
  const quarterly = periods.filter((p) => p.periodType === "quarterly").length;
  const monthly = periods.filter((p) => p.periodType === "monthly").length;
  const weekly = periods.filter((p) => p.periodType === "weekly").length;
  const rows: ExportSheet["rows"] = [
    ["Status", output?.status ?? ""],
    ["Estimated Insolvency Date", output?.estimatedInsolvencyDate ?? ""],
    ["Annual Periods", annual],
    ["Quarterly Periods", quarterly],
    ["Monthly Periods", monthly],
    ["Weekly Periods", weekly],
    ["Indicators Met", output?.totalIndicatorsMet ?? ""],
    ["Indicators Assessed", output?.totalIndicatorsAssessed ?? ""],
    ["Documents Analysed", output?.documentsAnalysed ?? ""],
  ];
  if (output?.agentNarrative)
    rows.push(["Agent Narrative", output.agentNarrative]);
  return {
    name: "Summary",
    omitFromWordPdf: true,
    headers: ["Field", "Value"],
    rows,
  };
}

function indicatorsSheet(
  indicators: FinancialIndicatorRow[],
): ExportSheet | null {
  if (indicators.length === 0) return null;
  const rows: ExportSheet["rows"] = indicators.map((i) => [
    CATEGORY_LABELS[i.category] ?? i.category,
    i.indicatorName,
    ASSESSMENT_LABELS[i.assessment] ?? i.assessment,
    i.confidence,
    i.assessment === "met" ? (i.earliestIndicationDate ?? "") : "",
    i.isEdited ? "Yes" : "",
    i.reasoning,
  ]);
  return {
    name: "Indicators",
    omitFromExcel: true,
    headers: [
      "Category",
      "Indicator",
      "Status",
      "Confidence",
      "Since",
      "Edited",
      "Analysis",
    ],
    rows,
  };
}

function indicatorEvidenceSheet(
  indicators: FinancialIndicatorRow[],
): ExportSheet | null {
  const rows: ExportSheet["rows"] = [];
  for (const i of indicators) {
    for (const ev of i.supportingEvidence ?? []) {
      rows.push([
        i.indicatorName,
        ev.periodLabel ?? "",
        ev.metric ?? "",
        ev.value ?? "",
        ev.note ?? "",
      ]);
    }
  }
  if (rows.length === 0) return null;
  return {
    name: "Indicator Evidence",
    headers: ["Indicator", "Period", "Metric", "Value", "Note"],
    rows,
  };
}

export function buildFinancialAnalysisExport(
  periods: FinancialPeriodEntryUI[],
  indicators: FinancialIndicatorRow[],
  output: FinancialSummaryOutput | undefined,
  /** MOR-810: the active analysis basis, stamped on the export title. */
  basis: "original" | "adjusted" = "original",
): ExportContent {
  const active = periods.filter((p) => p.status !== "merged");
  const sheets: ExportSheet[] = [summarySheet(output, active)];

  for (const type of ["annual", "quarterly", "monthly", "weekly"] as const) {
    const sheet = periodSheet(
      `${PERIOD_TYPE_TITLES[type]} Financial Data`,
      sortPeriods(active.filter((p) => p.periodType === type)),
    );
    if (sheet) sheets.push(sheet);
  }

  const ind = indicatorsSheet(indicators);
  if (ind) sheets.push(ind);
  const evidence = indicatorEvidenceSheet(indicators);
  if (evidence) sheets.push(evidence);

  return {
    title:
      basis === "adjusted"
        ? "Financial Analysis (Adjusted basis — practitioner adjustments applied)"
        : "Financial Analysis (Original basis)",
    sheets,
  };
}
